import { InferenceRule, InferredRelationshipData } from "../InferenceEngine";
import { KnowledgeGraph } from "@semantical/modules/knowledge-graph";
import Diagram from "@semantical/modules/diagram/Diagram";
import { isInverseObjectProperties, InverseObjectProperties, isSymmetricObjectProperty, SymmetricObjectProperty } from "@semantical/modules/owl2";
import { getPropertyIRI } from "@semantical/modules/owl2/types/PropertyExpression";
import { iriToString } from "@semantical/modules/owl2/types/IRI";
import type { IRI } from "@semantical/modules/owl2/types/IRI";

/**
 * Helper to convert IRI to a full URI string.
 * For abbreviated IRIs, we need to expand them using prefixes, but for now
 * we'll compare using the iriToString representation.
 */
function iriToFullString(iri: IRI, prefixes?: Map<string, string>): string {
    if (iri.kind === 'FullIRI') {
        return iri.value;
    }
    // For abbreviated IRIs, try to expand using prefixes
    if (prefixes) {
        // Find the namespace for this prefix
        for (const [namespace, prefix] of prefixes) {
            if (prefix === iri.prefix) {
                return namespace + iri.localName;
            }
        }
    }
    // Fallback to string representation
    return iriToString(iri);
}

/**
 * Inverse Property inference rule.
 *
 * Implements OWL2 owl:inverseOf and owl:SymmetricProperty semantics:
 *
 * 1. If P1 and P2 are declared as inverse properties (P1 owl:inverseOf P2),
 *    then for any relationship A-P1->B, we infer B-P2->A.
 *
 * 2. If P is declared as symmetric (P rdf:type owl:SymmetricProperty),
 *    then for any relationship A-P->B, we infer B-P->A.
 *
 * Examples:
 * - Ontology declares: hasParent owl:inverseOf hasChild
 *   Knowledge graph has: Alice-hasChild->Bob
 *   Rule infers: Bob-hasParent->Alice
 *
 * - Ontology declares: isMarriedWith rdf:type owl:SymmetricProperty
 *   Knowledge graph has: Alice-isMarriedWith->Bob
 *   Rule infers: Bob-isMarriedWith->Alice
 */
export class InversePropertyRule implements InferenceRule {
    id = "inverse-property";
    name = "Inverse Property Rule";
    description = "If P1 owl:inverseOf P2 and A-P1->B exists, infer B-P2->A. Also handles symmetric properties.";

    infer(knowledgeGraph: KnowledgeGraph, _diagram: Diagram): InferredRelationshipData[] {
        const inferred: InferredRelationshipData[] = [];

        // Get the ontology schema to access axioms
        const ontologySchema = knowledgeGraph.getOntologySchema();
        if (!ontologySchema) {
            console.debug("InversePropertyRule: No ontology schema available, skipping inference");
            return inferred;
        }
        if (!ontologySchema.ontology) {
            console.debug("InversePropertyRule: Ontology schema has no parsed ontology, skipping inference");
            return inferred;
        }

        const ontology = ontologySchema.ontology;
        const prefixes = ontologySchema.prefixes;

        console.debug(`InversePropertyRule: Ontology has ${ontology.axioms.length} axioms, schema has ${prefixes.size} prefixes`);

        // Build a bidirectional map of inverse property pairs
        // Map: property URI -> inverse property URI (both directions)
        const inverseMap = new Map<string, string>();

        for (const axiom of ontology.axioms) {
            if (isInverseObjectProperties(axiom)) {
                const inverseAxiom = axiom as InverseObjectProperties;

                // Get URIs for both properties
                const prop1IRI = getPropertyIRI(inverseAxiom.property1);
                const prop2IRI = getPropertyIRI(inverseAxiom.property2);

                const prop1Uri = iriToFullString(prop1IRI, prefixes);
                const prop2Uri = iriToFullString(prop2IRI, prefixes);

                // Store both directions
                inverseMap.set(prop1Uri, prop2Uri);
                inverseMap.set(prop2Uri, prop1Uri);

                console.debug(`InversePropertyRule: Found inverse pair ${prop1Uri} <-> ${prop2Uri}`);
            }

            // Also handle symmetric properties - they are their own inverse
            if (isSymmetricObjectProperty(axiom)) {
                const symmetricAxiom = axiom as SymmetricObjectProperty;
                const propIRI = getPropertyIRI(symmetricAxiom.property);
                const propUri = iriToFullString(propIRI, prefixes);

                // A symmetric property is its own inverse
                inverseMap.set(propUri, propUri);

                console.debug(`InversePropertyRule: Found symmetric property ${propUri}`);
            }
        }

        if (inverseMap.size === 0) {
            console.debug("InversePropertyRule: No inverse or symmetric property axioms found");
            return inferred;
        }

        // Get all existing relationships
        const relationships = Array.from(knowledgeGraph.relationships.values());
        console.debug(`InversePropertyRule: Checking ${relationships.length} relationships against ${inverseMap.size} inverse pairs`);

        // Log each relationship's predicate for debugging
        for (const rel of relationships) {
            console.debug(`InversePropertyRule: Relationship predicate: ${rel.predicate.uri}`);
        }

        // Build a set of existing relationship signatures for quick lookup
        // Signature format: "subjectUri|predicateUri|objectUri"
        const existingSignatures = new Set<string>();
        for (const rel of relationships) {
            existingSignatures.add(`${rel.subject.uri}|${rel.predicate.uri}|${rel.object.uri}`);
        }

        // Also track what we've already inferred to avoid duplicates
        const inferredSignatures = new Set<string>();

        // For each relationship, check if we can infer an inverse
        for (const rel of relationships) {
            const predicateUri = rel.predicate.uri;
            const inverseUri = inverseMap.get(predicateUri);

            if (inverseUri) {
                // We found an inverse property declaration
                // Infer: object-inverseProperty->subject
                const inferredSubjectUri = rel.object.uri;
                const inferredPredicateUri = inverseUri;
                const inferredObjectUri = rel.subject.uri;

                const signature = `${inferredSubjectUri}|${inferredPredicateUri}|${inferredObjectUri}`;

                // Check if this relationship already exists or has already been inferred
                if (!existingSignatures.has(signature) && !inferredSignatures.has(signature)) {
                    inferred.push({
                        subjectUri: inferredSubjectUri,
                        predicateUri: inferredPredicateUri,
                        objectUri: inferredObjectUri,
                        ruleId: this.id
                    });
                    inferredSignatures.add(signature);

                    console.debug(
                        `InversePropertyRule: Inferred ${inferredSubjectUri} -${inferredPredicateUri}-> ${inferredObjectUri}`
                    );
                }
            }
        }

        return inferred;
    }
}

export default InversePropertyRule;
